import React, { useMemo } from 'react';
import Markdown from 'markdown-to-jsx';
import { defaultComponents as C, headingComponents as H } from './components';
import { parseHTMLWithIcons } from '../../../../Icon/utils';
import { parseHTMLWithDisclaimers } from '../../../../Disclaimer/utils';
import { sanitizeHTML } from './sanitize';

interface MarkdownRendererProps {
  text: string;
  listStart?: number;
  isPreview?: boolean;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  text,
  listStart,
  isPreview = false,
}) => {
  const processedText = useMemo(() => {
    const textWithIcons = parseHTMLWithIcons(text);
    const textWithDisclaimers = parseHTMLWithDisclaimers(textWithIcons);
    return sanitizeHTML(textWithDisclaimers);
  }, [text]);

  let firstListRendered = false;

  return (
    <Markdown
      options={{
        forceWrapper: true,
        overrides: {
          a: {
            component: C.a,
          },
          p: {
            component: (props: any) => <C.p {...props} isPreview={isPreview} />,
          },
          h1: { component: C.h1 },
          h2: { component: C.h2 },
          h3: { component: C.h3 },
          h4: { component: C.h4 },
          h5: { component: C.h5 },
          h6: { component: C.h6 },
          ul: {
            component: ({ children, ...props }: any) => {
              const isFirstList = !firstListRendered;
              if (isFirstList) firstListRendered = true;

              return <C.ul {...props}>{children}</C.ul>;
            },
          },
          ol: {
            component: ({ children, ...props }: any) => {
              const isFirstList = !firstListRendered;
              if (isFirstList) firstListRendered = true;

              return (
                <C.ol
                  start={listStart && isFirstList ? listStart : undefined}
                  {...props}
                >
                  {children}
                </C.ol>
              );
            },
          },
          li: { component: C.li },
          sup: { component: C.sup },
          sub: { component: C.sub },
          Icon: {
            component: C.icon,
          },
          img: {
            component: C.img,
          },
          div: {
            component: ({ children, ...props }: any) => {
              return (
                <C.p {...props} isWrapper={true}>
                  {children}
                </C.p>
              );
            },
          },
          DisclaimerMarker: {
            component: C.disclaimerMarker,
          },
          ...(isPreview
            ? {
                em: {
                  component: ({ children, ...props }: any) => {
                    return <span {...props}>{children}</span>;
                  },
                },
              }
            : {}),
        },
      }}
    >
      {processedText}
    </Markdown>
  );
};

export interface RenderMarkdownOptions {
  text: string;
  listStart?: number;
  isPreview?: boolean;
}

/**
 * Renders markdown with custom components and handles list continuity.
 *
 * @param options - Options object containing:
 *   - text: Markdown text to render
 *   - listStart: undefined (no list processing), -1 (unordered list), or number (ordered list continuation)
 *   - isPreview: Whether to render in preview mode
 */
export const renderMarkdown = (options: RenderMarkdownOptions) => {
  const { text, listStart, isPreview } = options;
  return (
    <MarkdownRenderer text={text} listStart={listStart} isPreview={isPreview} />
  );
};

interface HeadingRendererProps {
  text: string;
  level: 1 | 2 | 3 | 4 | 5 | 6;
  forceHeading?: boolean;
  isPreview?: boolean;
  customStyle?: {};
}

const HeadingRenderer: React.FC<HeadingRendererProps> = ({
  text,
  level = 2,
  forceHeading = false,
  isPreview = false,
  customStyle = {},
}) => {
  const processedText = useMemo(() => {
    const textWithIcons = parseHTMLWithIcons(text);
    const textWithDisclaimers = parseHTMLWithDisclaimers(textWithIcons);
    
    return sanitizeHTML(textWithDisclaimers);
  }, [text]);

  const headingTag = `h${level}`;
  const HeadingComponent = H[headingTag as keyof typeof H];

  // Helper to wrap heading component with isPreview prop
  const wrapHeadingComponent = (Component: any) => {
    return (props: any) => (
      <Component {...props} isPreview={isPreview} customStyle={customStyle} />
    );
  };

  const overrides: any = {
    h1: {
      component: forceHeading
        ? wrapHeadingComponent(HeadingComponent)
        : wrapHeadingComponent(C.h1),
    },
    h2: {
      component: forceHeading
        ? wrapHeadingComponent(HeadingComponent)
        : wrapHeadingComponent(C.h2),
    },
    h3: {
      component: forceHeading
        ? wrapHeadingComponent(HeadingComponent)
        : wrapHeadingComponent(C.h3),
    },
    h4: {
      component: forceHeading
        ? wrapHeadingComponent(HeadingComponent)
        : wrapHeadingComponent(C.h4),
    },
    h5: {
      component: forceHeading
        ? wrapHeadingComponent(HeadingComponent)
        : wrapHeadingComponent(C.h5),
    },
    h6: {
      component: forceHeading
        ? wrapHeadingComponent(HeadingComponent)
        : wrapHeadingComponent(C.h6),
    },
    p: { component: wrapHeadingComponent(HeadingComponent) },
    a: { component: C.a },
    sup: { component: C.sup },
    sub: { component: C.sub },
    Icon: {
      component: C.icon,
    },
    img: {
      component: C.img,
    },
    DisclaimerMarker: {
      component: C.disclaimerMarker,
    },
  };

  return (
    <Markdown
      options={{
        overrides,
      }}
    >
      {processedText}
    </Markdown>
  );
};

export interface RenderAsHeadingOptions {
  text: string;
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  forceHeading?: boolean;
  isPreview?: boolean;
  customStyle?: {};
}

export const renderAsHeading = (options: RenderAsHeadingOptions) => {
  const {
    text,
    level = 2,
    forceHeading = false,
    isPreview = false,
    customStyle = {},
  } = options;

  return (
    <HeadingRenderer
      text={text}
      level={level}
      forceHeading={forceHeading}
      isPreview={isPreview}
      customStyle={customStyle}
    />
  );
};
